import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff, ArrowRight, Building2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import InstallAppButton from "@/components/InstallAppButton";

export default function Login() {
    const { user, checking, login, registerCompany } = useAuth();
    const navigate = useNavigate();
    // ?vt=<token> comes from the "Verified staff" page → pre-fill staff ID + email.
    // ?employee_id=XXX is the QR on cards printed before the verify page → send to verify.
    const [params] = useSearchParams();
    const verifyToken = params.get("vt") || "";
    const legacyCardNo = verifyToken ? "" : (params.get("employee_id") || "").trim().toUpperCase();
    const [mode, setMode] = useState("login"); // "login" | "register"
    const [audience, setAudience] = useState(params.get("audience") === "employee" || verifyToken ? "employee" : "admin"); // "admin" | "employee"
    const companyParam = (params.get("company") || "").trim().toUpperCase();
    const [companyId, setCompanyId] = useState(companyParam || localStorage.getItem("hrm_last_company_id") || "");
    const [brand, setBrand] = useState(null);
    const [employeeId, setEmployeeId] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!companyParam) return;
        api.get(`/companies/${encodeURIComponent(companyParam)}/branding`)
            .then(({ data }) => data?.exists && setBrand(data))
            .catch(() => {});
    }, [companyParam]);

    useEffect(() => {
        if (!verifyToken) return;
        api.get(`/public/staff/${encodeURIComponent(verifyToken)}/login-hint`)
            .then(({ data }) => {
                if (data.needs_activation) {
                    navigate(`/activate/${encodeURIComponent(verifyToken)}`, { replace: true });
                    return;
                }
                setEmployeeId(data.employee_no || "");
                setEmail(data.email || "");
                if (data.company_id) {
                    api.get(`/companies/${encodeURIComponent(data.company_id)}/branding`)
                        .then(({ data: b }) => b?.exists && setBrand(b)).catch(() => {});
                }
            })
            .catch(() => {});
    }, [verifyToken, navigate]);

    if (legacyCardNo) return <Navigate to={`/verify?no=${encodeURIComponent(legacyCardNo)}`} replace />;
    if (!checking && user) return <Navigate to="/dashboard" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        let res;
        if (audience === "employee") {
            res = await login("", email.trim(), password, employeeId.trim().toUpperCase());
        } else {
            res = await login(companyId.trim().toUpperCase(), email.trim(), password);
        }
        setLoading(false);
        if (res.ok) {
            if (audience === "admin") {
                localStorage.setItem("hrm_last_company_id", companyId.trim().toUpperCase());
            }
            navigate("/dashboard", { replace: true });
        } else {
            setError(res.error || "Login failed");
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-bone" data-testid="login-page">
            {/* Left: hero */}
            <div className="hidden lg:flex relative overflow-hidden bg-[#0E1630] text-white" data-testid="login-hero">
                <img
                    src="https://images.unsplash.com/photo-1759310610775-b298f34f73aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Nigerian professionals at work"
                    className="absolute inset-0 w-full h-full object-cover opacity-45 mix-blend-luminosity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E1630] via-[#0E1630]/75 to-[#0E1630]/30" />
                <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-klein/40 blur-3xl" />
                <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-terracotta/25 blur-3xl" />
                <div className="relative w-full flex flex-col justify-between p-12 xl:p-16">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#ffffff] flex items-center justify-center">
                            <span className="font-display font-extrabold text-klein text-xl">H</span>
                        </div>
                        <div>
                            <div className="font-display font-bold text-xl leading-none">Suntosh HRM</div>
                            <div className="text-xs text-white/60 mt-1">People &amp; payroll workspace</div>
                        </div>
                    </div>

                    <div className="max-w-xl">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white/90">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Built for Nigerian teams
                        </div>
                        <h1 className="mt-6 font-display font-extrabold text-5xl xl:text-6xl tracking-[-0.04em] leading-[1.02]">
                            Your people,<br />paid right.<br />
                            <span className="text-terracotta">Every month.</span>
                        </h1>
                        <p className="mt-6 text-white/75 text-base leading-relaxed max-w-md">
                            Staff records, leave, attendance and payroll with PAYE &amp; pension worked out for you — each company in its own private workspace.
                        </p>

                        <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
                            {[
                                ["PAYE & Pension", "Calculated each run"],
                                ["Payslips", "PDF in one click"],
                                ["Staff portal", "Leave, loans, clock-in"],
                            ].map(([t, d]) => (
                                <div key={t} className="rounded-2xl bg-white/[0.07] border border-white/10 backdrop-blur-md p-4">
                                    <div className="text-sm font-semibold">{t}</div>
                                    <div className="mt-1 text-xs text-white/60">{d}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="text-xs text-white/40">Suntosh HRM · Your data stays yours</div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center p-6 sm:p-12 bg-bone">
                {mode === "login" ? (
                    <form onSubmit={onSubmit} className="w-full max-w-md animate-fade-in-up" data-testid="login-form">
                        {brand?.name ? (
                            <div className="flex items-center gap-3 mb-8 rounded-2xl bg-white border border-ink/10 p-3 pr-5 w-fit" data-testid="login-company-brand">
                                {brand.logo_data_url ? (
                                    <img src={brand.logo_data_url} alt={brand.name} className="w-12 h-12 object-contain rounded-xl" data-testid="login-company-logo" />
                                ) : (
                                    <div className="w-12 h-12 rounded-xl bg-klein flex items-center justify-center"><span className="font-display font-extrabold text-white text-xl">{brand.name[0].toUpperCase()}</span></div>
                                )}
                                <div>
                                    <div className="text-[11px] text-ink/50">Signing in to</div>
                                    <div className="font-display font-bold text-ink leading-tight" data-testid="login-company-name">{brand.name}</div>
                                </div>
                            </div>
                        ) : (
                        <div className="lg:hidden flex items-center gap-2 mb-8">
                            <div className="w-9 h-9 rounded-xl bg-klein flex items-center justify-center"><span className="font-display font-extrabold text-white">H</span></div>
                            <span className="font-display font-bold text-lg text-ink">Suntosh HRM</span>
                        </div>
                        )}
                        <h2 className="font-display font-extrabold text-4xl lg:text-5xl tracking-[-0.035em] leading-[1.05] text-ink">
                            Welcome back<span className="text-terracotta">.</span>
                        </h2>
                        <p className="text-sm text-ink/60 mt-4 leading-relaxed">
                            {audience === "admin"
                                ? "Enter your Company ID along with your email and password to access your team's HR workspace."
                                : "Enter your Employee ID (staff number), email and password to access your portal."}
                        </p>

                        {/* Audience toggle */}
                        <div className="mt-7 grid grid-cols-2 p-1 rounded-xl bg-ink/5 border border-ink/10 max-w-xs" data-testid="login-audience-toggle">
                            <button
                                type="button"
                                onClick={() => setAudience("admin")}
                                data-testid="login-audience-admin"
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${audience === "admin" ? "bg-white text-ink shadow-card" : "text-ink/55 hover:text-ink"}`}
                            >
                                Admin / HR
                            </button>
                            <button
                                type="button"
                                onClick={() => setAudience("employee")}
                                data-testid="login-audience-employee"
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${audience === "employee" ? "bg-white text-ink shadow-card" : "text-ink/55 hover:text-ink"}`}
                            >
                                Employee
                            </button>
                        </div>

                        <div className="mt-8 space-y-5">
                            {audience === "admin" ? (
                                <div>
                                    <label className="overline">Company ID</label>
                                    <input
                                        type="text"
                                        value={companyId}
                                        onChange={(e) => setCompanyId(e.target.value.toUpperCase())}
                                        required
                                        autoFocus={!companyId}
                                        data-testid="login-company-id-input"
                                        className="mt-2 w-full border border-ink/15 bg-white py-3 px-4 rounded-xl focus:border-klein focus:outline-none text-ink placeholder:text-ink/30 uppercase tracking-wider"
                                        placeholder="e.g. ACME"
                                    />
                                    <p className="text-xs text-ink/40 mt-1.5">Don&apos;t have one? <button type="button" onClick={() => setMode("register")} className="text-klein hover:underline" data-testid="open-register-link">Create company →</button></p>
                                </div>
                            ) : (
                                <div>
                                    <label className="overline">Employee ID (staff number)</label>
                                    <input
                                        type="text"
                                        value={employeeId}
                                        onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                                        required
                                        autoFocus
                                        data-testid="login-employee-id-input"
                                        className="mt-2 w-full border border-ink/15 bg-white py-3 px-4 rounded-xl focus:border-klein focus:outline-none text-ink placeholder:text-ink/30 uppercase tracking-wider"
                                        placeholder="e.g. EG092601"
                                    />
                                    <p className="text-xs text-ink/40 mt-1.5">Need to activate? <Link to="/signup" className="text-klein hover:underline" data-testid="activate-employee-link">Activate account →</Link></p>
                                </div>
                            )}
                            <div>
                                <label className="overline">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoFocus={!!companyId}
                                    data-testid="login-email-input"
                                    className="mt-2 w-full border border-ink/15 bg-white py-3 px-4 rounded-xl focus:border-klein focus:outline-none text-ink placeholder:text-ink/30"
                                    placeholder="you@company.com"
                                />
                            </div>
                            <div>
                                <label className="overline">Password</label>
                                <div className="relative">
                                    <input
                                        type={showPass ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        data-testid="login-password-input"
                                        className="mt-2 w-full border border-ink/15 bg-white py-3 pl-4 pr-11 rounded-xl focus:border-klein focus:outline-none text-ink placeholder:text-ink/30"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPass((s) => !s)}
                                        className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink"
                                        tabIndex={-1}
                                        aria-label="Toggle password visibility"
                                    >
                                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <div className="mt-3 text-right">
                                    <Link to="/forgot-password" className="text-xs uppercase tracking-[0.18em] text-ink/50 hover:text-klein" data-testid="login-forgot-link">
                                        Forgot password?
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div
                                className="mt-6 border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive rounded-md"
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="mt-8 group w-full bg-klein text-white px-6 py-4 font-medium flex items-center justify-between hover:bg-klein-700 disabled:opacity-60 transition-colors rounded-xl shadow-card hover:shadow-lift"
                        >
                            <span>{loading ? "Signing in…" : "Sign in"}</span>
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </button>

                        <div className="mt-8 pt-6 border-t border-ink/10 text-center space-y-2">
                            <p className="text-xs text-ink/60">
                                Already on payroll?{" "}
                                <Link to="/signup" className="text-klein font-medium hover:underline" data-testid="login-employee-signup-link">
                                    Activate your employee account →
                                </Link>
                            </p>
                            <p className="text-xs text-ink/50">
                                New to Suntosh HRM?{" "}
                                <button type="button" onClick={() => setMode("register")} className="text-klein font-medium hover:underline" data-testid="footer-register-link">
                                    Create your company →
                                </button>
                            </p>
                            <p className="text-[11px] text-ink/35">
                                Platform owner?{" "}
                                <Link to="/super-admin/login" className="uppercase tracking-[0.18em] text-ink/50 hover:text-klein font-medium" data-testid="login-super-admin-link">
                                    Super-admin sign in
                                </Link>
                            </p>
                            <InstallAppButton variant="light" className="mt-2" />
                        </div>
                    </form>
                ) : (
                    <RegisterCompanyForm
                        onCancel={() => setMode("login")}
                        onSuccess={(cid, email) => {
                            setCompanyId(cid);
                            setEmail(email);
                            setMode("login");
                            setPassword("");
                            toast.success(`Company '${cid}' created — sign in below`);
                        }}
                        registerCompany={registerCompany}
                    />
                )}
            </div>
        </div>
    );
}

function RegisterCompanyForm({ onCancel, onSuccess, registerCompany }) {
    const [form, setForm] = useState({
        companyId: "",
        companyName: "",
        address: "",
        adminName: "",
        adminEmail: "",
        adminPassword: "",
        adminPasswordConfirm: "",
    });
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.adminPassword !== form.adminPasswordConfirm) {
            setError("Passwords don't match");
            return;
        }
        if (form.adminPassword.length < 8) {
            setError("Password must be at least 8 characters");
            return;
        }
        setLoading(true);
        const res = await registerCompany({
            companyId: form.companyId.toUpperCase(),
            companyName: form.companyName,
            address: form.address,
            adminName: form.adminName,
            adminEmail: form.adminEmail,
            adminPassword: form.adminPassword,
        });
        setLoading(false);
        if (res.ok) {
            onSuccess(form.companyId.toUpperCase(), form.adminEmail);
        } else {
            setError(res.error || "Registration failed");
        }
    };

    return (
        <form onSubmit={onSubmit} className="w-full max-w-md animate-fade-in-up" data-testid="register-form">
            <div className="flex items-center justify-between mb-4">
                <div className="overline text-terracotta flex items-center gap-2"><Plus className="w-3 h-3" /> New company</div>
                <button type="button" onClick={onCancel} className="text-ink/40 hover:text-ink" aria-label="Cancel" data-testid="register-cancel-btn">
                    <X className="w-5 h-5" />
                </button>
            </div>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-[1.05] text-ink flex items-center gap-3">
                <Building2 className="w-9 h-9 text-klein" strokeWidth={1.5} /> Create company
            </h2>
            <p className="text-sm text-ink/60 mt-3 leading-relaxed">
                Each company gets its own isolated workspace. Pick a Company ID — your team will use it to log in.
            </p>

            <div className="mt-7 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="overline">Company ID</label>
                        <input
                            type="text"
                            required
                            value={form.companyId}
                            onChange={(e) => setForm((f) => ({ ...f, companyId: e.target.value.toUpperCase() }))}
                            placeholder="ACME"
                            data-testid="register-company-id"
                            className="mt-2 w-full border border-ink/15 bg-white py-2.5 px-4 rounded-xl focus:border-klein focus:outline-none text-ink uppercase tracking-wider"
                        />
                        <p className="text-[10px] text-ink/40 mt-1">letters, numbers, hyphens only</p>
                    </div>
                    <div>
                        <label className="overline">Company name</label>
                        <input type="text" required value={form.companyName} onChange={set("companyName")} placeholder="Acme Logistics" data-testid="register-company-name" className="mt-2 w-full border border-ink/15 bg-white py-2.5 px-4 rounded-xl focus:border-klein focus:outline-none text-ink" />
                    </div>
                </div>
                <div>
                    <label className="overline">Address (optional)</label>
                    <input type="text" value={form.address} onChange={set("address")} placeholder="Lagos, Nigeria" data-testid="register-address" className="mt-2 w-full border border-ink/15 bg-white py-2.5 px-4 rounded-xl focus:border-klein focus:outline-none text-ink" />
                </div>
                <div className="pt-3 border-t border-ink/10">
                    <div className="overline mb-3 text-ink/50">Your admin account</div>
                    <div className="space-y-4">
                        <input type="text" required value={form.adminName} onChange={set("adminName")} placeholder="Your full name" data-testid="register-admin-name" className="w-full border border-ink/15 bg-white py-2.5 px-4 rounded-xl focus:border-klein focus:outline-none text-ink" />
                        <input type="email" required value={form.adminEmail} onChange={set("adminEmail")} placeholder="you@company.com" data-testid="register-admin-email" className="w-full border border-ink/15 bg-white py-2.5 px-4 rounded-xl focus:border-klein focus:outline-none text-ink" />
                        <div className="relative">
                            <input type={showPass ? "text" : "password"} required value={form.adminPassword} onChange={set("adminPassword")} placeholder="Password (min 8 chars)" data-testid="register-admin-password" className="w-full border border-ink/15 bg-white py-2.5 pl-4 pr-11 rounded-xl focus:border-klein focus:outline-none text-ink" />
                            <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-ink/40 hover:text-ink" tabIndex={-1}>
                                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <input type={showPass ? "text" : "password"} required value={form.adminPasswordConfirm} onChange={set("adminPasswordConfirm")} placeholder="Confirm password" data-testid="register-admin-password-confirm" className="w-full border border-ink/15 bg-white py-2.5 px-4 rounded-xl focus:border-klein focus:outline-none text-ink" />
                    </div>
                </div>
            </div>

            {error && (
                <div className="mt-5 border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive rounded-md" data-testid="register-error">
                    {error}
                </div>
            )}

            <button
                type="submit"
                disabled={loading}
                data-testid="register-submit-button"
                className="mt-7 group w-full bg-klein text-white px-6 py-4 font-medium flex items-center justify-between hover:bg-klein-700 disabled:opacity-60 transition-colors rounded-xl shadow-card hover:shadow-lift"
            >
                <span>{loading ? "Creating company…" : "Create company & continue"}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
        </form>
    );
}
